// client/src/pages/ResetPasswordPage.js
import React, { useState, useEffect } from 'react';
import {
  Form, Button, Card, Spinner, Row, Col, Image, Alert
} from 'react-bootstrap';
import { useParams, useNavigate, Link } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import { InputGroup } from 'react-bootstrap';
import logo from '../assets/logo-c.png';

// Use the environment variable for the API root URL
const API_ROOT_URL = process.env.REACT_APP_API_ROOT_URL;

function ResetPasswordPage() {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Inline validation error states
  const [passwordError, setPasswordError] = useState('');
  const [confirmPasswordError, setConfirmPasswordError] = useState('');
  
  // Track successful reset
  const [isResetSuccessful, setIsResetSuccessful] = useState(false);

  const { token } = useParams();
  const navigate = useNavigate();

  // Clear any messages when the component first mounts
  useEffect(() => {
    setMessage('');
    setError('');
  }, []);

  // Inline validation handlers
  const validatePassword = (value) => {
    if (value.length < 6) {
      setPasswordError('Password must be at least 6 characters long.');
      return false;
    }
    setPasswordError('');
    return true;
  };

  const validateConfirmPassword = (value) => {
    if (value && value !== password) {
      setConfirmPasswordError('Passwords do not match.');
      return false;
    }
    setConfirmPasswordError('');
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');
    setError('');

    // Re-validate all fields on submit to catch any final errors
    const isPasswordValid = validatePassword(password);
    const isConfirmPasswordValid = validateConfirmPassword(confirmPassword);

    if (!isPasswordValid || !isConfirmPasswordValid) {
      setError('Please correct the errors in the form.');
      setLoading(false);
      return;
    }

    if (!API_ROOT_URL) {
      setError('Server configuration error. Please try again later.');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_ROOT_URL}/auth/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token, newPassword: password }),
      });

      const result = await response.json();

      if (response.ok) {
        setMessage(result.message);
        setIsResetSuccessful(true); // Set success state
        // Redirect to the login page after a few seconds
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else {
        setError(result.message || 'An unknown error occurred.');
      }

    } catch (err) {
      console.error('Error in reset password request:', err);
      setError('Failed to connect to the server. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-vh-100 d-flex flex-column justify-content-center align-items-center p-3"
      style={{ background: 'linear-gradient(#4594ad, white, white)' }}
    >
      {/* Logo + Branding */}
      <div className="text-center mb-0 mt-0">
        <Image
          src={logo}
          alt="Company Logo"
          className="mb-3 img-fluid"
          style={{ maxWidth: '250px', height: 'auto' }}
        />
        <h1 className="mt-5 mb-0 text-black">Touchless Tracking</h1>
      </div>

      {/* Reset Password Card */}
      <Card className="p-4 mt-4 shadow rounded border" style={{ maxWidth: '450px', width: '100%' }}>
        <Card.Body>
          <h4 className="text-center mb-4">Reset Password</h4>

          <Form onSubmit={handleSubmit}>
            <p className="text-center text-muted">
              Enter your new password below.
            </p>
            <Form.Group className="mb-3" controlId="formNewPassword">
              <Form.Label>New Password</Form.Label>
              <InputGroup>
                <Form.Control
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter new password (min 6 characters)"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    validatePassword(e.target.value);
                    validateConfirmPassword(confirmPassword);
                  }}
                  isInvalid={!!passwordError}
                  disabled={loading || isResetSuccessful}
                  required
                />
                <Button
                  variant="outline-secondary"
                  onClick={() => setShowPassword(prev => !prev)}
                  disabled={loading || isResetSuccessful}
                >
                  {showPassword ? 'Hide' : 'Show'}
                </Button>
                <Form.Control.Feedback type="invalid">{passwordError}</Form.Control.Feedback>
              </InputGroup>
            </Form.Group>

            <Form.Group className="mb-3" controlId="formConfirmNewPassword">
              <Form.Label>Confirm Password</Form.Label>
              <InputGroup>
                <Form.Control
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    validateConfirmPassword(e.target.value);
                  }}
                  isInvalid={!!confirmPasswordError}
                  disabled={loading || isResetSuccessful}
                  required
                />
                <Button
                  variant="outline-secondary"
                  onClick={() => setShowConfirmPassword(prev => !prev)}
                  disabled={loading || isResetSuccessful}
                >
                  {showConfirmPassword ? 'Hide' : 'Show'}
                </Button>
                <Form.Control.Feedback type="invalid">{confirmPasswordError}</Form.Control.Feedback>
              </InputGroup>
            </Form.Group>

            {message && (
              <Alert variant="success" className="text-center">
                {message}
              </Alert>
            )}
            {error && (
              <Alert variant="danger" className="text-center">
                {error}
              </Alert>
            )}

            <Button
              variant="primary"
              type="submit"
              className="w-100 mt-3 fw-bold"
              disabled={loading || isResetSuccessful}
            >
              {loading ? (
                <>
                  <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                  <span className="ms-2">Resetting...</span>
                </>
              ) : (
                'Reset Password'
              )}
            </Button>
          </Form>

          <Row className="mt-4">
            <Col className="text-center">
              <small>
                Return to{' '}
                <Link to="/login" className="fw-bold text-decoration-none">Login</Link>
              </small>
            </Col>
          </Row>
        </Card.Body>
      </Card>
    </div>
  );
}

export default ResetPasswordPage;
